import { useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, CircleCheck, FileDown, MessageSquare, RotateCcw, SearchX, ShieldAlert, StickyNote } from 'lucide-react'
import { ApiError } from '@/api/request'
import type { TicketDetail } from '@/api/ticketsApi'
import { Page } from '@/components/layout/Page'
import { EmptyState, ErrorState, LoadingState, errorMessage } from '@/components/common/States'
import { AttachmentsTab } from '@/components/ticket/AttachmentsTab'
import { AuditTab } from '@/components/ticket/AuditTab'
import { Composer } from '@/components/ticket/Composer'
import { ResolveDialog } from '@/components/ticket/ResolveDialog'
import { AssigneeMenu, PriorityMenu, StatusMenu } from '@/components/ticket/TicketQuickFields'
import { EscalateDialog, ExportDialog, ReopenDialog } from '@/components/ticket/TicketActionDialogs'
import { PendingUploadsBanner } from '@/components/ticket/PendingUploadsBanner'
import { Thread, ThreadLegend } from '@/components/ticket/Thread'
import { TicketRail } from '@/components/ticket/TicketRail'
import { Button } from '@/components/ui/button'
import { useDownloadAttachment } from '@/hooks/useAttachments'
import { useMessages } from '@/hooks/useMessages'
import { useNow } from '@/hooks/useNow'
import { useTicket } from '@/hooks/useTickets'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { buildThread, replyTarget, viewerTimeZone, whenLabel, type ThreadView } from '@/lib/threadView'
import { typeLabel } from '@/lib/ticketLabels'
import { cn } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'

type TabKey = 'conversation' | 'full' | 'customer' | 'notes' | 'audit' | 'attachments'

const pill = 'inline-block rounded-sm px-2 py-[3px] text-label'

/** Artboards 02 (open) and 05 (resolved). Full-bleed: header, then thread + right rail. */
export default function TicketDetailPage() {
  const { t } = useTranslation()
  const { id = '' } = useParams()
  const ticket = useTicket(id)

  if (ticket.isPending) {
    return (
      <Page>
        <LoadingState />
      </Page>
    )
  }
  if (ticket.isError) {
    const notFound = ticket.error instanceof ApiError && (ticket.error.status === 404 || ticket.error.status === 403)
    // Missing or not yours is a dead end, not a failure: no retry, a way back instead.
    if (notFound) {
      return (
        <Page>
          <EmptyState
            icon={SearchX}
            title={t('ticket.notFoundTitle')}
            message={t('ticket.notFound', { id })}
            action={
              <Button asChild variant="outline">
                <Link to="/queue">
                  <ArrowLeft aria-hidden="true" />
                  {t('ticket.backToQueue')}
                </Link>
              </Button>
            }
          />
        </Page>
      )
    }
    return (
      <Page>
        <ErrorState message={t('ticket.loadError', { message: errorMessage(ticket.error) })} onRetry={() => void ticket.refetch()} />
      </Page>
    )
  }
  return <TicketView ticket={ticket.data} routeId={id} />
}

function TicketView({ ticket, routeId }: { ticket: TicketDetail; routeId: string }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const isAgent = user?.kind === 'AGENT'
  const timeZone = useMemo(() => viewerTimeZone(), [])
  const now = useNow(30_000)

  const can = {
    reply: usePermission(PERMISSIONS.messagesReply),
    notesRead: usePermission(PERMISSIONS.notesRead),
    notesWrite: usePermission(PERMISSIONS.notesWrite),
    audit: usePermission(PERMISSIONS.auditRead),
    attachRead: usePermission(PERMISSIONS.attachmentsRead),
    attachUpload: usePermission(PERMISSIONS.attachmentsUpload),
    link: usePermission(PERMISSIONS.ticketsLinkRecord),
    resolve: usePermission(PERMISSIONS.ticketsResolve),
    reopen: usePermission(PERMISSIONS.ticketsReopen),
    escalate: usePermission(PERMISSIONS.ticketsEscalateDeviation),
    export: usePermission(PERMISSIONS.ticketsExport),
    exportInternal: usePermission(PERMISSIONS.ticketsExportInternal),
    assign: usePermission(PERMISSIONS.ticketsAssign),
    update: usePermission(PERMISSIONS.ticketsUpdate),
  }
  const showNotes = isAgent && can.notesRead

  const done = ticket.status === 'resolved' || ticket.status === 'closed'
  const [searchParams, setSearchParams] = useSearchParams()
  const [dialog, setDialog] = useState<'resolve' | 'reopen' | 'escalate' | 'export' | null>(null)
  const [composerOpen, setComposerOpen] = useState<null | 'reply' | 'note'>(null)

  // Tabs: open ticket = Conversation / Internal notes; resolved = Full thread / Customer replies only.
  const tabs: { key: TabKey; label: string; count?: number }[] = []
  if (!showNotes) {
    tabs.push({ key: 'conversation', label: t('ticket.tabs.conversation') })
  } else if (done) {
    tabs.push({ key: 'full', label: t('ticket.tabs.full'), count: ticket.counts.messages })
    tabs.push({ key: 'customer', label: t('ticket.tabs.customer'), count: ticket.counts.customerMessages })
  } else {
    tabs.push({ key: 'conversation', label: t('ticket.tabs.conversation') })
    tabs.push({ key: 'notes', label: t('ticket.tabs.notes'), count: ticket.internalNotes ?? 0 })
  }
  if (isAgent && can.audit) tabs.push({ key: 'audit', label: t('ticket.tabs.audit'), count: ticket.counts.auditEntries })
  if (can.attachRead) tabs.push({ key: 'attachments', label: t('ticket.tabs.attachments'), count: ticket.counts.attachments })

  const requested = searchParams.get('tab') as TabKey | null
  const tab: TabKey = tabs.find((x) => x.key === requested)?.key ?? tabs[0].key
  const setTab = (key: TabKey) => {
    const next = new URLSearchParams(searchParams)
    if (key === tabs[0].key) next.delete('tab')
    else next.set('tab', key)
    setSearchParams(next, { replace: true })
  }

  const threadTab = tab === 'conversation' || tab === 'full' || tab === 'customer' || tab === 'notes'
  // Agents load the full thread once and filter per tab; customers can only ever get the customer view.
  const messages = useMessages(routeId, isAgent ? 'full' : 'customer')
  const download = useDownloadAttachment(routeId)
  const view: ThreadView = isAgent ? (tab as ThreadView) : 'customer'
  const items = useMemo(
    () => (messages.data && threadTab ? buildThread(messages.data, ticket, view, timeZone) : []),
    [messages.data, threadTab, ticket, view, timeZone],
  )

  const reporter = ticket.reporter
  const opened = reporter
    ? reporter.role && !done
      ? t('ticket.openedWithRole', { time: whenLabel(ticket.createdAt, timeZone, now), name: reporter.name, role: reporter.role })
      : t('ticket.opened', { time: whenLabel(ticket.createdAt, timeZone, now), name: reporter.name })
    : null
  const resolvedLine =
    done && ticket.resolution
      ? t('ticket.resolvedBy', { time: whenLabel(ticket.resolution.resolvedAt, timeZone, now), name: ticket.resolution.resolvedBy.name })
      : null

  const composerAuthor = isAgent ? 'agent' : 'customer'
  const replyTo = replyTarget(messages.data ?? [], reporter?.name)

  return (
    <div className="flex min-h-full flex-col">
      <header className="flex items-start justify-between gap-6 border-b border-line px-10 pb-6 pt-7">
        <div className="flex min-w-0 flex-col gap-2.5">
          {/* The top bar carries the trail back to the queue; the key stays as the eyebrow. */}
          <span className="font-mono text-meta text-ink-muted">{ticket.key}</span>
          <h1 className="max-w-[640px] text-[28px] font-semibold leading-[34px] tracking-[-0.02em] text-ink">{ticket.subject}</h1>
          <div className="flex flex-wrap items-center gap-2">
            <PriorityMenu ticket={ticket} routeId={routeId} canUpdate={isAgent && can.update} />
            <StatusMenu
              ticket={ticket}
              routeId={routeId}
              can={{ update: isAgent && can.update, resolve: isAgent && can.resolve, reopen: isAgent && can.reopen }}
              onResolve={() => setDialog('resolve')}
              onReopen={() => setDialog('reopen')}
            />
            {isAgent ? <AssigneeMenu ticket={ticket} routeId={routeId} canAssign={can.assign} /> : null}
            <span className={cn(pill, 'bg-status-neutral-bg font-medium text-status-neutral-fg')}>
              {t('ticket.productType', { product: ticket.product.name, type: typeLabel(ticket.type) })}
            </span>
            {ticket.followUpOf ? (
              <Link to={`/tickets/${ticket.followUpOf.key}`} className={cn(pill, 'bg-status-progress-bg font-medium text-status-progress-fg hover:underline')}>
                {t('ticket.followUpOf', { key: ticket.followUpOf.key })}
              </Link>
            ) : null}
            {ticket.duplicateOf ? (
              <Link to={`/tickets/${ticket.duplicateOf.key}`} className={cn(pill, 'bg-status-neutral-bg font-medium text-status-neutral-fg hover:underline')}>
                {t('ticket.duplicateOf', { key: ticket.duplicateOf.key })}
              </Link>
            ) : null}
            {opened ? (
              <span className="pl-1 text-meta text-ink-muted">{[opened, resolvedLine].filter(Boolean).join(' · ')}</span>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2.5">
          {done ? (
            <>
              {can.export ? (
                <Button type="button" variant="outline" onClick={() => setDialog('export')}>
                  <FileDown aria-hidden="true" />
                  {t('ticket.actions.export')}
                </Button>
              ) : null}
              {isAgent && can.reopen && ticket.status === 'resolved' ? (
                <Button type="button" variant="outline" onClick={() => setDialog('reopen')}>
                  <RotateCcw aria-hidden="true" />
                  {t('ticket.actions.reopen')}
                </Button>
              ) : null}
            </>
          ) : isAgent ? (
            <>
              {can.escalate ? (
                <Button type="button" variant="outline" onClick={() => setDialog('escalate')}>
                  <ShieldAlert aria-hidden="true" />
                  {t('ticket.actions.escalate')}
                </Button>
              ) : null}
              {can.resolve ? (
                <Button type="button" onClick={() => setDialog('resolve')}>
                  <CircleCheck aria-hidden="true" />
                  {t('ticket.actions.resolve')}
                </Button>
              ) : null}
            </>
          ) : null}
        </div>
      </header>

      <div className="flex min-h-0 grow">
        <div className="flex min-w-0 grow flex-col gap-6 px-10 py-7">
          <div role="tablist" aria-label={t('ticket.eyebrow')} className="flex gap-6 border-b border-line">
            {tabs.map((x) => {
              const active = x.key === tab
              return (
                <button
                  key={x.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(x.key)}
                  className={cn(
                    '-mb-px rounded-t-sm pb-2.5 text-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    active ? 'border-b-2 border-primary font-semibold text-primary' : 'border-b-2 border-transparent text-ink-muted hover:text-ink',
                  )}
                >
                  {x.count === undefined ? x.label : t('ticket.tabs.withCount', { label: x.label, count: x.count })}
                </button>
              )
            })}
          </div>

          <PendingUploadsBanner ticketId={ticket.id} routeId={routeId} />

          {threadTab ? (
            <>
              {done && isAgent ? <ThreadLegend /> : null}
              {messages.isPending ? (
                <LoadingState />
              ) : messages.isError ? (
                <ErrorState
                  message={t('ticket.message.loadError', { message: errorMessage(messages.error) })}
                  onRetry={() => void messages.refetch()}
                />
              ) : items.length === 0 ? (
                <EmptyState
                  compact
                  icon={tab === 'notes' ? StickyNote : MessageSquare}
                  message={tab === 'notes' ? t('ticket.message.emptyNotes') : t('ticket.message.empty')}
                />
              ) : (
                <Thread items={items} timeZone={timeZone} assigneeId={ticket.assignee?.id} onDownload={(aid) => download.mutate(aid)} />
              )}
              {download.error ? <ErrorState message={errorMessage(download.error)} /> : null}
              <div className="grow" />
              <div className="sticky bottom-0 bg-background pb-1 pt-2">
                {done && composerOpen === null ? (
                  <ClosedBar
                    status={ticket.status}
                    canNote={isAgent && can.notesWrite}
                    canReply={!isAgent && can.reply}
                    onNote={() => setComposerOpen('note')}
                    onReply={() => setComposerOpen('reply')}
                  />
                ) : (can.reply || can.notesWrite) ? (
                  <Composer
                    key={`${composerOpen ?? 'open'}-${tab}`}
                    routeId={routeId}
                    author={composerAuthor}
                    canReply={can.reply && (!done || !isAgent)}
                    canNote={isAgent && can.notesWrite}
                    canAttach={can.attachUpload}
                    canLink={isAgent && can.link}
                    replyTo={replyTo}
                    initialMode={composerOpen === 'note' || tab === 'notes' ? 'note' : 'reply'}
                    autoFocus={composerOpen !== null}
                    showHint={!done}
                    onSent={() => (done ? setComposerOpen(null) : undefined)}
                  />
                ) : null}
              </div>
            </>
          ) : tab === 'audit' ? (
            <AuditTab routeId={routeId} timeZone={timeZone} />
          ) : (
            <AttachmentsTab routeId={routeId} timeZone={timeZone} />
          )}
        </div>

        <TicketRail
          ticket={ticket}
          routeId={routeId}
          now={now}
          canAssign={isAgent && can.assign}
          canUpdate={can.update}
          canResolve={can.resolve}
          canReopen={can.reopen}
          isAgent={isAgent}
          onResolve={() => setDialog('resolve')}
          onReopen={() => setDialog('reopen')}
        />
      </div>

      {isAgent && can.resolve && !done ? (
        <ResolveDialog ticket={ticket} routeId={routeId} open={dialog === 'resolve'} onOpenChange={(o) => setDialog(o ? 'resolve' : null)} />
      ) : null}
      {isAgent && can.escalate ? (
        <EscalateDialog ticket={ticket} routeId={routeId} open={dialog === 'escalate'} onOpenChange={(o) => setDialog(o ? 'escalate' : null)} />
      ) : null}
      {isAgent && can.reopen ? (
        <ReopenDialog ticket={ticket} routeId={routeId} open={dialog === 'reopen'} onOpenChange={(o) => setDialog(o ? 'reopen' : null)} />
      ) : null}
      {can.export ? (
        <ExportDialog
          routeId={routeId}
          canInternal={isAgent && can.exportInternal}
          open={dialog === 'export'}
          onOpenChange={(o) => setDialog(o ? 'export' : null)}
        />
      ) : null}
    </div>
  )
}

/** Artboard 05's dashed bar under a resolved / closed thread. */
function ClosedBar({
  status,
  canNote,
  canReply,
  onNote,
  onReply,
}: {
  status: string
  canNote: boolean
  canReply: boolean
  onNote: () => void
  onReply: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="mt-2 flex items-center justify-between rounded-lg border border-dashed border-line-strong px-4 py-3.5">
      <span className="text-body text-ink-muted">{status === 'closed' ? t('ticket.closed.closed') : t('ticket.closed.resolved')}</span>
      {canNote ? (
        <button type="button" onClick={onNote} className="text-meta font-semibold text-ink hover:underline">
          {t('ticket.closed.addNote')}
        </button>
      ) : canReply ? (
        <button type="button" onClick={onReply} className="text-meta font-semibold text-ink hover:underline">
          {t('ticket.closed.reply')}
        </button>
      ) : null}
    </div>
  )
}
